import React, { useState, useEffect, useRef } from 'react';
import {
  NavLink, Link, useRouteMatch, useLocation,
} from 'react-router-dom';

import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import isNil from 'lodash/isNil';
import FontSizeOption from '../blocks/FontSizeOption.jsx';

import Padding from './Padding.jsx';

import { env } from '../../config';
import useHttpStatus from '~/commons/hooks/useHttpStatus';

const showRankings = () => {
  const { user, school } = env;

  if (!user.roles.includes('student')) return false;
  if (!school) return false;

  const {
    settings: { studentRanking, schoolClassRanking },
  } = school;

  if (
    [
      ...Object.values(studentRanking),
      ...Object.values(schoolClassRanking),
    ].every((b) => !b)
  ) return false;

  return true;
};

const links = [
  { to: '/app/announcements', children: '公告欄' },
];

// Reusable LoginComponent that accepts stored prop
const LoginComponent = ({ stored }) => {
  const userName = (stored && stored.name && stored.name.full && stored.name.full.zhhk)
    || (stored && stored.name && stored.name.email)
    || '用戶';
  let avatarSrc = '/assets/img/userInfo.svg';
  if (stored && stored.sex === 'male') avatarSrc = '/assets/img/male_sex.svg';
  else if (stored && stored.sex === 'female') avatarSrc = '/assets/img/female_sex.svg';
  return (
    <Link className="d-flex align-items-center" to="/app/profile">
      <img
        src={avatarSrc}
        alt="User Icon"
        className="d-block"
        style={{ width: 32, height: 32 }}
      />
      <span
        style={{
          fontSize: '16px',
          marginLeft: '8px',
          fontWeight: 400,
          color: '#DE6400',
        }}
      >
        {userName}
      </span>
    </Link>
  );
};

const Nav = () => {
  const [show, setShow] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isCompetitionPg = useRouteMatch('/competition');
  const location = useLocation();
  const { user } = env;
  const [res, req] = useHttpStatus();
  const [stored, store] = useState(null);

  useEffect(() => {
    (async () => {
      const { payload: u } = await req({ url: '/api/core/users/self' });
      store(u);
    })();
  }, []);

  useEffect(() => {
    const handleUserUpdate = () => {
      req({ url: '/api/core/users/self' }).then(({ payload: u }) => {
        store(u);
      });
    };
    window.addEventListener('userUpdated', handleUserUpdate);
    return () => window.removeEventListener('userUpdated', handleUserUpdate);
  }, []);

  useEffect(() => {
    function setContentPadding() {
      const nav = document.querySelector('.header-nav');
      const content = document.querySelector('.content');
      if (nav && content) {
        content.style.paddingTop = `${nav.offsetHeight}px`;
      }
    }
    setContentPadding();
    setTimeout(()=>setContentPadding(),500); // Set again after delay.
    window.addEventListener('resize', setContentPadding);
    return () => window.removeEventListener('resize', setContentPadding);
  }, []);

  return (
    <nav
      className={`header-nav py-0 bg-white ${show ? 'active' : ''}`}
      style={{ zIndex: 100 }}
    >
      <Padding>
        {/* Desktop version (≥1200px) - Responsive within single layout */}
        <div className="d-none d-xxl-block">
          <div className="row align-items-center mb-2">
            <div className="col-12 d-flex flex-row justify-content-between align-items-center">
              {/* Left side: four images */}
              <div className="d-flex align-items-center nav-desktop-logos">
                <div className="nav-desktop-logo d-flex align-items-center gap-2">
                  <img
                    src="/assets/img/boc.svg"
                    alt="中國銀行(香港)"
                    className="d-block"
                  />
                  <span style={{ fontSize: '14px' }}>呈獻</span>
                </div>
                <div className="nav-desktop-logo">
                  <img
                    src="/assets/img/logo.svg"
                    alt="Academy"
                    className="d-block"
                  />
                </div>
                <div className="nav-desktop-logo d-flex align-items-center gap-2">
                  <img
                    src="/assets/img/aocs.svg"
                    alt="中華文化教育"
                    className="d-block"
                  />
                  <span style={{ fontSize: '14px' }}>主辦</span>
                </div>
                <div className="nav-desktop-logo d-flex align-items-center gap-2">
                  <img
                    src="/assets/img/edb.svg"
                    alt="香港特別行政區政府教育局"
                    className="d-block"
                  />
                  <span style={{ fontSize: '14px' }}>支持</span>
                </div>
              </div>
              {/* Right side: actions */}
              <div className="d-flex align-items-center nav-desktop-actions">
                {/* 任務階段 button */}
                <Link
                  className="btn btn-link d-flex align-items-center"
                  style={{
                    color: location.pathname === '/app' ? '#FF831E' : '#888',
                    fontWeight: location.pathname === '/app' ? 700 : 500,
                    whiteSpace: 'nowrap',
                  }}
                  to="/app"
                >
                  <img
                    src={location.pathname === '/app' ? '/assets/img/training-active.svg' : '/assets/img/training.svg'}
                    alt="任務階段"
                    className="nav-desktop-icon"
                  />
                  <span className="nav-desktop-text">任務階段</span>
                </Link>
                {/* Navigation links from links array */}
                {links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    exact={link.exact}
                    className="d-flex align-items-center"
                    style={{
                      color: location.pathname === link.to ? '#FF831E' : '#888',
                      fontWeight: location.pathname === link.to ? 700 : 500,
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {link.children === '公告欄' && (
                      <img
                        src={location.pathname === link.to ? '/assets/img/announcement-active.svg' : '/assets/img/announcement.svg'}
                        alt="公告欄"
                        className="nav-desktop-icon"
                      />
                    )}
                    <span className="nav-desktop-text">{link.children}</span>
                  </Link>
                ))}
                {/* LoginComponent with user */}
                {!isNil(stored) && <LoginComponent stored={stored} />}
                {/* Logout link */}
                <a
                  href="/logout"
                  className="d-flex align-items-center"
                  style={{
                    color: '#888',
                    fontWeight: 500,
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span className="nav-desktop-text">登出</span>
                </a>
                {/* FontSizeOption */}
                <FontSizeOption />
              </div>
            </div>
          </div>
        </div>

        {/* iPad version - two rows */}
        <div className="d-none d-lg-block d-xxl-none">
          {/* First row: four images */}
          <div className="row align-items-center mb-3">
            <div className="col-12 d-flex flex-row justify-content-center align-items-center">
              <div className="d-flex align-items-center" style={{ gap: 20 }}>
                <div className="d-flex align-items-center gap-2">
                  <img
                    src="/assets/img/boc.svg"
                    alt="中國銀行(香港)"
                    className="d-block"
                  />
                  <span style={{ fontSize: '14px' }}>呈獻</span>
                </div>
                <img
                  src="/assets/img/logo.svg"
                  alt="Academy"
                  className="d-block"
                />
                <div className="d-flex align-items-center gap-2">
                  <img
                    src="/assets/img/aocs.svg"
                    alt="中華文化教育"
                    className="d-block"
                  />
                  <span style={{ fontSize: '14px' }}>主辦</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <img
                    src="/assets/img/edb.svg"
                    alt="香港特別行政區政府教育局"
                    className="d-block"
                  />
                  <span style={{ fontSize: '14px' }}>支持</span>
                </div>
              </div>
            </div>
          </div>
          {/* Second row: navigation and actions */}
          <div className="row align-items-center">
            <div className="col-12 d-flex flex-row justify-content-between align-items-center">
              {/* Left side: navigation links */}
              <div className="d-flex align-items-center" style={{ gap: 20 }}>
                {/* 任務階段 button */}
                <Link
                  className="btn btn-link d-flex align-items-center"
                  style={{
                    color: location.pathname === '/app' ? '#FF831E' : '#888',
                    fontWeight: location.pathname === '/app' ? 700 : 500,
                    fontSize: 16,
                  }}
                  to="/app"
                >
                  <img
                    src={location.pathname === '/app' ? '/assets/img/training-active.svg' : '/assets/img/training.svg'}
                    alt="任務階段"
                    style={{ width: 28, height: 28, marginRight: 6 }}
                  />
                  任務階段
                </Link>
                {/* Navigation links from links array */}
                {links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    exact={link.exact}
                    className="d-flex align-items-center"
                    style={{
                      color: location.pathname === link.to ? '#FF831E' : '#888',
                      fontWeight: location.pathname === link.to ? 700 : 500,
                      fontSize: 16,
                      textDecoration: 'none',
                    }}
                  >
                    {/* Optional: add icon for 公告欄 if desired */}
                    {link.children === '公告欄' && (
                      <img
                        src={location.pathname === link.to ? '/assets/img/announcement-active.svg' : '/assets/img/announcement.svg'}
                        alt="公告欄"
                        style={{ width: 28, height: 28, marginRight: 6 }}
                      />
                    )}
                    {link.children}
                  </Link>
                ))}
              </div>
              {/* Right side: user actions */}
              <div className="d-flex align-items-center" style={{ gap: 16 }}>
                {/* LoginComponent with user */}
                {!isNil(stored) && <LoginComponent stored={stored} />}
                {/* Logout link */}
                <a
                  href="/logout"
                  className="d-flex align-items-center"
                  style={{
                    color: '#888',
                    fontWeight: 500,
                    fontSize: 16,
                    textDecoration: 'none',
                  }}
                >
                  登出
                </a>
                {/* FontSizeOption */}
                <FontSizeOption />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile version */}
        <div className="d-lg-none">
          <div className="d-flex d-md-none row align-items-center mb-2">
            <div className="col p-1">
              <img
                src="/assets/img/boc_mobile_new.svg"
                alt="中國銀行(香港)"
                className="d-block nav-boc-img"
              />
            </div>
            <div className="col-auto p-1 nav-title-text">
              呈獻
            </div>          
            <div className="col p-1">
              <img
                src="/assets/img/logo.svg"
                alt="Academy"
                className="d-block nav-logo-img"
              />
            </div>
          </div>
          {/* First row: boc and logo */}
          <div className="d-none d-md-flex row align-items-center mb-2">
            <div className="col-12 d-flex flex-row align-items-center">
              <div className="d-flex align-items-center text-nowrap" style={{ gap: 8 }}>
                <img
                  src="/assets/img/boc_mobile_new.svg"
                  alt="中國銀行(香港)"
                  className="d-block nav-boc-img"
                />
                &nbsp;呈獻&nbsp;
                <img
                  src="/assets/img/logo.svg"
                  alt="Academy"
                  className="d-block nav-img"
                />
              </div>
            </div>
          </div>
          {/* Second row: hamburger/close, LoginComponent - always visible */}
          <div className="row align-items-center mb-2">
            <div className="col-12 d-flex flex-row justify-content-between align-items-center">
              {/* Left: Hamburger menu or close button */}
              {isMobileMenuOpen ? (
                <button
                  className="btn btn-link nav-toggle"
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{ fontSize: '1.5rem', color: '#FF831E' }}
                  aria-label="close menu"
                >
                  ✕
                </button>
              ) : (
                <button
                  className="btn btn-link nav-toggle"
                  onClick={() => setIsMobileMenuOpen(true)}
                  style={{ fontSize: '1.5rem', color: '#FF831E' }}
                  aria-label="menu"
                >
                  &#9776;
                </button>
              )}
              {/* Right: LoginComponent */}
              {!isNil(stored) && <LoginComponent stored={stored} />}
            </div>
          </div>
          {/* Mobile full-screen overlay menu */}
          {isMobileMenuOpen && (
            <div
              className="d-lg-none position-fixed w-100 h-100 bg-white"
              style={{
                top: 0,
                left: 0,
                zIndex: 99999,
                overflowY: 'auto',
              }}
            >
              <div className="header-nav py-3 bg-white">
                <div className="container-fluid">
                  <div className="d-flex d-md-none row align-items-center mb-2">
                    <div className="col p-1">
                      <img
                        src="/assets/img/boc_mobile_new.svg"
                        alt="中國銀行(香港)"
                        className="d-block nav-boc-img"
                      />
                    </div>
                    <div className="col-auto p-1 nav-title-text">
                      呈獻
                    </div>          
                    <div className="col p-1">
                      <img
                        src="/assets/img/logo.svg"
                        alt="Academy"
                        className="d-block nav-logo-img"
                      />
                    </div>
                  </div>
                  {/* First row: boc and logo */}
                  <div className="d-none d-md-flex row align-items-center mb-2">
                    <div className="col-12 d-flex flex-row align-items-center">
                      <div
                        className="d-flex align-items-center text-nowrap"
                        style={{ gap: 8 }}
                      >
                        <img
                          src="/assets/img/boc_mobile_new.svg"
                          alt="中國銀行(香港)"
                          className="d-block nav-boc-img"
                        />
                        &nbsp;呈獻&nbsp;
                        <img
                          src="/assets/img/logo.svg"
                          alt="Academy"
                          className="d-block nav-img"
                        />
                      </div>
                    </div>
                  </div>
                  {/* Second row: close button (left), LoginComponent (right) */}
                  <div className="row align-items-center mb-2">
                    <div className="col-12 d-flex flex-row justify-content-between align-items-center">
                      <button
                        className="btn btn-link nav-toggle"
                        onClick={() => setIsMobileMenuOpen(false)}
                        style={{ fontSize: '1.5rem', color: '#FF831E' }}
                        aria-label="close menu"
                      >
                        ✕
                      </button>
                      {!isNil(stored) && <LoginComponent stored={stored} />}
                    </div>
                  </div>
                </div>
              </div>
              <div
                className="container-fluid py-4"
                style={{ marginTop: '32%' }}
              >
                {/* Mobile nav links */}
                <div className="row">
                  <div className="col-12">
                    <nav className="nav flex-column gap-4">
                      {/* 任務階段 button - mobile */}
                      <Link
                        to="/app"
                        className="nav-link py-3 d-flex align-items-center justify-content-center"
                        style={{
                          fontSize: '18px',
                          color: location.pathname === '/app' ? '#FF831E' : '#333',
                          fontWeight: location.pathname === '/app' ? 700 : 500,
                          cursor: 'pointer',
                        }}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <img
                          src={location.pathname === '/app' ? '/assets/img/training-active.svg' : '/assets/img/training.svg'}
                          alt="任務階段"
                          style={{ width: 32, height: 32, marginRight: 8 }}
                        />
                        任務階段
                      </Link>
                      {links.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          className="nav-link py-3 d-flex align-items-center justify-content-center"
                          style={{
                            fontSize: '18px',
                            color: location.pathname === link.to ? '#FF831E' : '#333',
                            fontWeight: location.pathname === link.to ? 700 : 500,
                            cursor: 'pointer',
                          }}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {link.children === '公告欄' && (
                            <img
                              src={location.pathname === link.to ? '/assets/img/announcement-active.svg' : '/assets/img/announcement.svg'}
                              alt="公告欄"
                              style={{ width: 32, height: 32, marginRight: 8 }}
                            />
                          )}
                          {link.children}
                        </Link>
                      ))}
                    </nav>
                    {/* Logout link */}
                    <a
                      href="/logout"
                      className="nav-link py-3 d-flex align-items-center justify-content-center"
                      style={{
                        fontSize: '18px',
                        color: '#333',
                        textDecoration: 'none',
                      }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      登出
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Padding>
    </nav>
  );
};

export default Nav;
